import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Toggle, Badge } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../api/client';
import api from '../api/client';
import toast from 'react-hot-toast';

const NETWORKS = [
  { key: 'instagram', label: 'Instagram', icon: '📸', hint: 'App ID y App Secret de Meta for Developers' },
  { key: 'facebook', label: 'Facebook', icon: '👥', hint: 'Usa el mismo App ID y Secret de Instagram (misma app de Meta)' },
  { key: 'twitter', label: 'X / Twitter', icon: '🐦', hint: 'Client ID y Client Secret de developer.twitter.com' },
  { key: 'linkedin', label: 'LinkedIn', icon: '💼', hint: 'Client ID y Client Secret de developer.linkedin.com' },
  { key: 'youtube', label: 'YouTube', icon: '▶️', hint: 'Client ID y Client Secret de Google Cloud Console' },
  { key: 'tiktok', label: 'TikTok', icon: '🎵', hint: 'Client Key y Client Secret de developers.tiktok.com' },
  { key: 'google_calendar', label: 'Google Calendar', icon: '📅', hint: 'Client ID y Client Secret de Google Cloud Console (habilitar Calendar API + Meet)' },
  { key: 'gmail', label: 'Gmail', icon: '📧', hint: 'Client ID y Client Secret de Google Cloud Console (habilitar Gmail API). Puedes usar las mismas credenciales que Google Calendar.' },
];

function IntegrationsTab() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [telegramForm, setTelegramForm] = useState({ bot_token: '', chat_id: '', notification_email: '', notifications: {} });
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [show, setShow] = useState({});

  useEffect(() => {
    api.get('/integrations').then(({ data }) => {
      const map = {};
      data.forEach(d => { map[d.network] = { client_id: d.client_id, client_secret: '', is_enabled: d.is_enabled }; });
      setConfigs(map);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (network, key) => (val) => setConfigs(c => ({ ...c, [network]: { ...c[network], [key]: val } }));

  const handleSave = async (network) => {
    const cfg = configs[network] || {};
    if (!cfg.client_id || !cfg.client_secret) { toast.error('Client ID y Client Secret son requeridos'); return; }
    setSaving(network);
    try {
      await api.post('/integrations', { network, client_id: cfg.client_id, client_secret: cfg.client_secret, is_enabled: cfg.is_enabled ?? true });
      toast.success(`${network} configurado ✓`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(''); }
  };

  const handleDelete = async (network) => {
    try {
      await api.delete(`/integrations/${network}`);
      setConfigs(c => { const n = { ...c }; delete n[network]; return n; });
      toast.success(`${network} eliminado`);
    } catch { toast.error('Error al eliminar'); }
  };

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Cargando integraciones...</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
        Ingresa las credenciales OAuth de cada red social. Una vez guardadas, el botón "Conectar" en Cuentas funcionará automáticamente.
      </p>
      {NETWORKS.map(net => {
        const cfg = configs[net.key] || { client_id: '', client_secret: '', is_enabled: true };
        const saved = !!configs[net.key]?.client_id;
        return (
          <Card key={net.key} style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '22px' }}>{net.icon}</span>
                <div>
                  <span style={{ fontWeight: '700', fontSize: '14px' }}>{net.label}</span>
                  {saved && <Badge color="#10b981" style={{ marginLeft: '8px' }}>Configurado</Badge>}
                </div>
              </div>
              {saved && (
                <button onClick={() => handleDelete(net.key)} style={{ background: '#ef444415', border: '1px solid #ef444430', color: '#ef4444', cursor: 'pointer', padding: '4px 10px', borderRadius: '8px', fontSize: '12px' }}>Eliminar</button>
              )}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>💡 {net.hint}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Client ID / App ID</label>
                <input value={cfg.client_id || ''} onChange={e => set(net.key, 'client_id')(e.target.value)} placeholder={`${net.label} Client ID`} style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)', fontSize: '13px', padding: '8px 12px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Client Secret / App Secret</label>
                <input value={cfg.client_secret || ''} onChange={e => set(net.key, 'client_secret')(e.target.value)} type={show[net.key] ? 'text' : 'password'} placeholder={saved ? '••••••• (dejar vacío para mantener)' : `${net.label} Client Secret`} style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)', fontSize: '13px', padding: '8px 36px 8px 12px', outline: 'none', boxSizing: 'border-box' }} />
                <button type="button" onClick={() => setShow(s => ({ ...s, [net.key]: !s[net.key] }))} style={{ position: 'absolute', right: '8px', top: '24px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px' }}>{show[net.key] ? '🙈' : '👁'}</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="sm" loading={saving === net.key} onClick={() => handleSave(net.key)}>
                  {saved ? 'Actualizar' : 'Guardar'}
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export default function SettingsPage() {
  const { user, reloadUser } = useAuth();
  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState({ name: user?.name || '', email: user?.email || '' });
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [notif, setNotif] = useState(() => {
    try { const s = localStorage.getItem('notif_prefs'); return s ? JSON.parse(s) : { story_push: true, publish_success: true, publish_fail: true, analytics_weekly: false }; }
    catch { return { story_push: true, publish_success: true, publish_fail: true, analytics_weekly: false }; }
  });
  const [saving, setSaving] = useState('');
  const [telegramForm, setTelegramForm] = useState({ bot_token: '', chat_id: '', notification_email: '', notifications: {} });
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);

  const setP = (key) => (val) => setProfile(f => ({ ...f, [key]: val }));
  const setN = (key) => (val) => setNotif(f => ({ ...f, [key]: val }));
  const setPw = (key) => (val) => setPasswords(f => ({ ...f, [key]: val }));

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving('profile');
    try {
      await usersAPI.update(user.id, { name: profile.name, email: profile.email });
      await reloadUser();
      toast.success('Perfil actualizado ✓');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al guardar perfil'); }
    finally { setSaving(''); }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (passwords.newPass !== passwords.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    if (passwords.newPass.length < 8) { toast.error('Mínimo 8 caracteres'); return; }
    setSaving('password');
    try {
      await usersAPI.updatePassword(user.id, { current_password: passwords.current, new_password: passwords.newPass });
      setPasswords({ current: '', newPass: '', confirm: '' });
      toast.success('Contraseña actualizada ✓');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al cambiar contraseña'); }
    finally { setSaving(''); }
  };

  const handleSaveTelegram = async () => {
    setTelegramSaving(true);
    try {
      await api.put('/crm/tenants/mine/notifications', {
        telegram_bot_token: telegramForm.bot_token,
        telegram_chat_id: telegramForm.chat_id,
        notification_email: telegramForm.notification_email,
        telegram_notifications: telegramForm.notifications,
      });
      toast.success('Configuración de Telegram guardada ✓');
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    } finally { setTelegramSaving(false); }
  };

  const handleTestTelegram = async () => {
    if (!telegramForm.bot_token || !telegramForm.chat_id) {
      toast.error('Ingresa el Bot Token y Chat ID primero');
      return;
    }
    await handleSaveTelegram();
    setTelegramTesting(true);
    try {
      await api.post('/crm/tenants/mine/notifications/test-telegram');
      toast.success('✅ Mensaje de prueba enviado a Telegram');
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Error probando conexión');
    } finally { setTelegramTesting(false); }
  };

  const handleSaveNotif = async () => {
    localStorage.setItem('notif_prefs', JSON.stringify(notif));
    try {
      await api.patch('/auth/me/notifications', { analytics_weekly: notif.analytics_weekly });
      toast.success('Preferencias guardadas ✓');
    } catch {
      toast.success('Preferencias guardadas localmente ✓');
    }
  };

  const tabs = [
    { key: 'profile', label: '👤 Perfil' },
    { key: 'password', label: '🔒 Contraseña' },
    { key: 'notif', label: '🔔 Notificaciones' },
    { key: 'integrations', label: '🔗 Integraciones' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '680px' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ flex: 1, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: tab === t.key ? 'var(--bg-card)' : 'transparent', color: tab === t.key ? 'var(--text-primary)' : 'var(--text-muted)', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: tab === t.key ? '700' : '500', cursor: 'pointer', transition: 'all 0.2s', boxShadow: tab === t.key ? 'var(--shadow-sm)' : 'none' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <Card>
          <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: '800', color: '#fff' }}>
                {profile.name.charAt(0) || 'A'}
              </div>
              <div>
                <Badge color="var(--accent-primary)">{user?.role === 'admin' ? 'Administrador' : 'Operador'}</Badge>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Sesión activa</div>
              </div>
            </div>
            <Input label="Nombre" value={profile.name} onChange={setP('name')} placeholder="Tu nombre" />
            <Input label="Email" type="email" value={profile.email} onChange={setP('email')} placeholder="tu@email.com" />
            <Button type="submit" loading={saving === 'profile'} size="sm" style={{ alignSelf: 'flex-end' }}>Guardar perfil</Button>
          </form>
        </Card>
      )}

      {tab === 'password' && (
        <Card>
          <form onSubmit={handleSavePassword} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Input label="Contraseña actual" type="password" value={passwords.current} onChange={setPw('current')} placeholder="••••••••" />
            <Input label="Nueva contraseña" type="password" value={passwords.newPass} onChange={setPw('newPass')} placeholder="Mínimo 8 caracteres" />
            <Input label="Confirmar contraseña" type="password" value={passwords.confirm} onChange={setPw('confirm')} placeholder="Repite la nueva contraseña" error={passwords.confirm && passwords.newPass !== passwords.confirm ? 'Las contraseñas no coinciden' : ''} />
            <Button type="submit" loading={saving === 'password'} size="sm" style={{ alignSelf: 'flex-end' }}>Cambiar contraseña</Button>
          </form>
        </Card>
      )}

      {tab === 'notif' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* ── Sección Telegram ── */}
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700', marginBottom: '4px' }}>🤖 Notificaciones por Telegram</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>Recibe alertas en tiempo real y resumen diario en tu Telegram. Gratis y sin límites.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Bot Token</label>
                <input style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', fontSize: '13px' }}
                  type="password" value={telegramForm?.bot_token || ''} onChange={e => setTelegramForm(p => ({ ...p, bot_token: e.target.value }))} placeholder="1234567890:ABCdef..." />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Chat ID</label>
                <input style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', fontSize: '13px' }}
                  value={telegramForm?.chat_id || ''} onChange={e => setTelegramForm(p => ({ ...p, chat_id: e.target.value }))} placeholder="123456789" />
              </div>
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Email para recordatorios de citas</label>
              <input style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', fontSize: '13px' }}
                type="email" value={telegramForm?.notification_email || ''} onChange={e => setTelegramForm(p => ({ ...p, notification_email: e.target.value }))} placeholder="admin@tuempresa.com" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', margin: 0 }}>Eventos a notificar:</p>
              {[
                { key: 'new_lead', label: '🎯 Nuevo lead registrado' },
                { key: 'buy_intent', label: '🔥 Intención de compra detectada' },
                { key: 'appointment', label: '📅 Cita agendada + recordatorio 30min antes' },
                { key: 'reservation', label: '🗓 Nueva reserva creada' },
                { key: 'payment', label: '💳 Pago recibido (anticipo o saldo)' },
                { key: 'cancellation', label: '🔴 Solicitud de cancelación con reembolso' },
                { key: 'daily_summary', label: '📊 Resumen diario a las 7am' },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px' }}>{item.label}</span>
                  <Toggle checked={telegramForm?.notifications?.[item.key] !== false} onChange={v => setTelegramForm(p => ({ ...p, notifications: { ...(p.notifications || {}), [item.key]: v } }))} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
              <Button variant="ghost" size="sm" onClick={handleTestTelegram} loading={telegramTesting}>🔍 Probar conexión</Button>
              <Button size="sm" onClick={handleSaveTelegram} loading={telegramSaving}>Guardar</Button>
            </div>
          </div>
        </Card>
        {/* ── Preferencias originales ── */}
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { key: 'story_push', label: 'Stories listos para publicar', desc: 'Notificación push cuando un story esté listo' },
              { key: 'publish_success', label: 'Publicación exitosa', desc: 'Cuando se publica correctamente en todas las redes' },
              { key: 'publish_fail', label: 'Error de publicación', desc: 'Cuando falla alguna red social' },
              { key: 'analytics_weekly', label: 'Resumen semanal', desc: 'Reporte de analytics cada lunes' },
            ].map(item => (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{item.label}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{item.desc}</div>
                </div>
                <Toggle checked={notif[item.key]} onChange={setN(item.key)} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
              <Button size="sm" onClick={handleSaveNotif}>Guardar preferencias</Button>
            </div>
          </div>
        </Card>
        </div>
      )}

      {tab === 'integrations' && <IntegrationsTab />}
    </div>
  );
}
